import _ from 'lodash';
import {
  ActionParamSchema, BrokerNode, BrokerOptions, Context, GenericObject, RegistryDiscovererOptions,
  ServiceBroker, ServiceSchema
} from 'moleculer';

import Configs from './configs';
import Logger from './logger';
import { MoleculerLogger } from './moleculerLogger';
import FbxServicesContext from './serviceContext';
import ServiceDiscoverer from './serviceDiscoverer';

export const singletonEnforcer = Symbol('singletonEnforcer');

export interface IMoleculerConfigs<C = Record<string, unknown>> {
  enabled: boolean;
  broker?: ServiceBroker;
  brokerOptions?: BrokerOptions;
  schema?: Partial<ServiceSchema>;
  context?: C;
}

export type IMoleculerCallback<T, P = unknown, M extends object = {}> = (ctx: Context<P, M>) => Promise<T>;
export type IMoleculerCallbackWithSchema<T, P = unknown, M extends object = {}> = {
  params: ActionParamSchema;
  handler: (ctx: Context<P, M>) => Promise<T>;
};

export interface IMoleculerActionInfo {
  name: string;
  count: number;
  hasLocal: boolean;
  available: boolean;
  action: {
    rawName: string;
    name: string;
  };
}

export default class Services {
  static get broker(): ServiceBroker {
    return Services.instance.broker;
  }

  static get init() {
    return Services.instance.init.bind(Services.instance);
  }

  static get start() {
    return Services.instance.start.bind(Services.instance);
  }

  static get stop() {
    return Services.instance.stop.bind(Services.instance);
  }

  static get call() {
    return Services.instance.call.bind(Services.instance);
  }

  static get emit() {
    return Services.instance.emit.bind(Services.instance);
  }

  static get broadcast() {
    return Services.instance.broadcast.bind(Services.instance);
  }

  static get broadcastLocal() {
    return Services.instance.broadcastLocal.bind(Services.instance);
  }

  static get createService() {
    return Services.instance.createService.bind(Services.instance);
  }

  private static namespace = 'fbx';

  private static singleton: Services;

  private broker: ServiceBroker;

  public context: IMoleculerConfigs['context'];

  /**
  * Represents an instance of the application configurations handler
  * @constructor
  * */
  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) { throw new Error('Cannot construct singleton'); }
  }

  // static get context(): IMoleculerConfigs['context'] {
  //   return Services.instance.context;
  // }

  /**
  * Returns the current instance
  * @return { Services } - Current Services instance
  * */
  static get instance(): Services {
    if (!Services.singleton) {
      Services.singleton = new Services(singletonEnforcer);
    }
    return Services.singleton;
  }

  static set instance(instance: Services) {
    Services.singleton = instance;
  }

  public init(configs: IMoleculerConfigs): void {
    const { broker, schema, brokerOptions, context } = configs;
    const { fbxfile } = Configs.configs;

    this.context = context;

    if (broker) {
      this.broker = broker;
    } else {
      const tcpDiscoveryNodeId = process.env.TCP_DISCOVERY_NODE_ID;
      const tcpDiscoveryPort = process.env.TCP_DISCOVERY_PORT;
      const useHostname = (process.env.MOLECULER_USE_HOSTNAME === 'true');
      let tcpDiscoveryUrl: string;

      if (tcpDiscoveryNodeId && tcpDiscoveryPort) {
        const tcpDiscoveryHost = process.env.TCP_DISCOVERY_HOST || '127.0.0.1';
        tcpDiscoveryUrl = `${tcpDiscoveryHost}:${tcpDiscoveryPort}/${tcpDiscoveryNodeId}`;
        Logger.silly('Using custom tcpDiscoveryUrl', tcpDiscoveryUrl);
      }

      const udpDiscovery = (process.env.UDP_DISCOVERY === 'true');
      Logger.silly('udpDiscovery', udpDiscovery);
      const baseBrokerOptions = tcpDiscoveryUrl ? {
        transporter: {
          type: 'TCP',
          options: {
            udpDiscovery,
            urls: [tcpDiscoveryUrl],
            useHostname,
          },
        },
      } : {
        transporter: 'TCP',
      };

      const options = {
        logger: new MoleculerLogger({}),
        namespace: Services.namespace,
        nodeID: fbxfile.name,
        ContextFactory: FbxServicesContext,
        registry: {
          discoverer: 'Local',
          useHostname,
        },
        ...baseBrokerOptions,
        ...brokerOptions
      };

      if (typeof options.registry.discoverer === 'string' && options.registry.discoverer === 'Local') {
        options.registry.discoverer = new ServiceDiscoverer({ tcpDiscoveryNodeId });
      } else if (_.isPlainObject(options.registry.discoverer) && (options.registry.discoverer as RegistryDiscovererOptions).type === 'Local') {
        options.registry.discoverer = new ServiceDiscoverer({
          tcpDiscoveryNodeId,
          ...(options.registry.discoverer as RegistryDiscovererOptions).options
        });
      }

      // Create a ServiceBroker
      this.broker = new ServiceBroker(options);
    }

    this.broker.createService({
      ...schema,
      name: Configs.configs.fbxfile.name,
    });
  }

  public async start(): Promise<void> {
    return this.broker.start();
  }

  public async stop(): Promise<void> {
    return this.broker.stop();
  }

  public getLocalNodeInfo(): Partial<BrokerNode> {
    if (this.broker) {
      return {
        ...this.broker.getLocalNodeInfo(),
        id: this.broker.nodeID,
      };
    }
    return {};
  }

  public async call<T, D = Record<string, unknown>, M extends object = {}>(action: string, params?: D, meta?: M): Promise<T | void> {
    try {
      if (this.broker) {
        const isActionAvailable = await this.isActionAvailable(action);
        if (isActionAvailable) {
          const res = await this.broker.call<T | void, D | {}>(action, params || {}, meta || {});
          return res;
        }
        Logger.warn(`Action '${action}' is not available`);
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  public async emit<T, D = Record<string, unknown>>(eventName: string, data?: D, opts?: GenericObject): Promise<T | void> {
    try {
      if (this.broker) {
        const res = await this.broker.emit(`${Services.namespace}.${eventName}`, { name: Configs.configs.fbxfile.name, ...(data || {}) }, opts);
        return res;
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  public async broadcast<T, D = Record<string, unknown>>(eventName: string, data?: D, opts?: GenericObject): Promise<T | void> {
    try {
      if (this.broker) {
        const res = await this.broker.broadcast(`${Services.namespace}.${eventName}`, { name: Configs.configs.fbxfile.name, ...(data || {}) }, opts);
        return res;
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  public async broadcastLocal<T, D = Record<string, unknown>>(eventName: string, data?: D, opts?: GenericObject): Promise<T | void> {
    try {
      if (this.broker) {
        const res = await this.broker.broadcastLocal(`${Services.namespace}.${eventName}`, { name: Configs.configs.fbxfile.name, ...(data || {}) }, opts);
        return res;
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  public createService(schema: ServiceSchema): void {
    this.broker.createService({
      ...schema,
      name: `${Configs.configs.fbxfile.name}.${schema.name}`,
    });
  }

  private async availableActions(): Promise<IMoleculerActionInfo[]> {
    return this.broker?.call<IMoleculerActionInfo[]>('$node.actions');
  }

  async isActionAvailable(actionName: string): Promise<boolean> {
    const actions = (await this.availableActions()) || [];
    const availableActionNames = actions.map((a) => a.name);
    return availableActionNames.includes(actionName);
  }
}
